<template>
  <div class="step-panel" data-testid="payroll-step5-summary">
    <!-- Step Header -->
    <div class="step-header">
      <h2>{{ t('Conciliação de Pagamentos') }}</h2>
    </div>

    <!-- Helper Banner -->
    <div class="helper-banner">
      <i class="pi pi-info-circle"></i>
      <div class="helper-content">
        <strong>{{ t('Cálculos de Pagamento') }}</strong>
        <ul>
          <li>{{ t('Nesta etapa, os valores de salário serão calculados com base nas regras definidas') }}</li>
          <li>{{ t('O sistema processará comissões sobre executado, vendido remotamente e valores fixos') }}</li>
          <li>{{ t('Revise os resultados cuidadosamente antes de confirmar e criar as despesas') }}</li>
        </ul>
      </div>
    </div>

    <!-- Warning Banner -->
    <div class="warning-banner">
      <i class="pi pi-exclamation-triangle"></i>
      <div class="warning-content">
        <strong>{{ t('Atenção') }}</strong>
        <p>{{ t('Após iniciar os cálculos, o período será marcado como "calculado". Certifique-se de que todas as etapas anteriores estão corretas antes de prosseguir.') }}</p>
      </div>
    </div>

    <!-- Important Notes -->
    <div class="important-notes">
      <div class="notes-header">
        <i class="pi pi-info-circle"></i>
        <h3>{{ t('Próximos Passos') }}</h3>
      </div>
      <ul class="notes-list">
        <li>
          <i class="pi pi-check-circle"></i>
          <span>{{ t('Clique em "Iniciar Cálculos" para processar todos os pagamentos') }}</span>
        </li>
        <li>
          <i class="pi pi-check-circle"></i>
          <span>{{ t('Após os cálculos, revise os valores na próxima etapa antes de confirmar') }}</span>
        </li>
        <li>
          <i class="pi pi-check-circle"></i>
          <span>{{ t('Uma vez confirmado, as despesas de salário serão criadas com status "pendente"') }}</span>
        </li>
      </ul>
    </div>

    <p class="step-description">
      {{ t('Revise o status dos pagamentos e gerencie as despesas de salário') }}
    </p>

    <!-- Conciliation Status Card -->
    <div class="conciliation-card">
      <div class="card-body">
        <!-- Main Summary Grid -->
        <div class="summary-grid">
          <div class="summary-item">
            <div class="item-label">{{ t('Status') }}</div>
            <div class="item-value">
              <span class="status-indicator" :class="statusClass"></span>
              {{ t(statusText) }}
              <status-tag v-if="statusAsNumber === PERIOD_STATUS.CONFIRMED" value="Já Confirmado" severity="warning" class="confirmed-badge" />
            </div>
          </div>

          <div class="summary-item">
            <div class="item-label">{{ t('Regras Calculadas') }}</div>
            <div class="item-value">
              {{ paymentSummary.totals.calculated || 0 }} / {{ paymentSummary.totals.count || 0 }}
            </div>
          </div>

          <div class="summary-item highlight">
            <div class="item-label">{{ t('Total a Pagar') }}</div>
            <div class="item-value">{{ n(parseFloat(String(paymentSummary.totals.net_amount ?? 0)) || 0, 'currency') }}</div>
          </div>
        </div>

        <!-- User-Centered Breakdown with Collapsible Panels -->
        <div v-if="paymentSummary.by_user && paymentSummary.by_user.length > 0" class="breakdown-section">
          <h4 class="breakdown-title">{{ t('Pagamentos por Colaborador') }}</h4>
          <Accordion :multiple="true" class="user-accordion">
            <AccordionTab v-for="user in paymentSummary.by_user" :key="String(user.user_id)">
              <template #header>
                <div class="user-header">
                  <div class="user-info">
                    <i class="pi pi-user"></i>
                    <span class="user-name">{{ user.user_name || t('Colaborador') }}</span>
                  </div>
                  <status-tag
                    :value="n(parseFloat(String(user.total ?? 0)) || 0, 'currency')"
                    severity="success"
                    class="user-total-tag" />
                </div>
              </template>

              <!-- User's executions table -->
              <data-table
                :config="getUserExecutionsConfig(user)"
                :data="user.executions || []"
                class="executions-table" />
            </AccordionTab>
          </Accordion>

          <!-- Totals Summary -->
          <div class="totals-summary">
            <div class="totals-row">
              <span class="totals-label">{{ t('Total Colaboradores') }}</span>
              <span class="totals-value">{{ paymentSummary.by_user.length }}</span>
            </div>
            <div class="totals-row highlight">
              <span class="totals-label">{{ t('Total a Pagar') }}</span>
              <span class="totals-value">{{ n(parseFloat(String(paymentSummary.totals.net_amount ?? 0)) || 0, 'currency') }}</span>
            </div>
          </div>
        </div>

        <!-- Rule Types Breakdown (secondary view) -->
        <div v-else-if="paymentSummary.ordered_steps.length > 0" class="breakdown-section">
          <h4 class="breakdown-title">{{ t('Detalhamento por Tipo') }}</h4>
          <div class="breakdown-table">
            <div class="breakdown-header">
              <span class="col-type">{{ t('Tipo') }}</span>
              <span class="col-count">{{ t('Regras') }}</span>
              <span class="col-status">{{ t('Status') }}</span>
              <span class="col-amount">{{ t('Valor') }}</span>
            </div>
            <div
              v-for="step in paymentSummary.ordered_steps"
              :key="step.rule_type"
              class="breakdown-row"
              :class="{ 'is-complete': step.is_complete }">
              <span class="col-type">
                <i :class="getRuleTypeIcon(step.rule_type)" class="type-icon"></i>
                {{ step.label }}
              </span>
              <span class="col-count">{{ step.count }}</span>
              <span class="col-status">
                <span v-if="step.is_complete" class="status-badge complete">
                  <i class="pi pi-check"></i> {{ t('Calculado') }}
                </span>
                <span v-else class="status-badge pending">
                  <i class="pi pi-clock"></i> {{ t('Pendente') }}
                </span>
              </span>
              <span class="col-amount">{{ n(parseFloat(String(step.net_amount ?? 0)) || 0, 'currency') }}</span>
            </div>
            <!-- Totals Row -->
            <div class="breakdown-row totals-row">
              <span class="col-type"><strong>{{ t('Total') }}</strong></span>
              <span class="col-count"><strong>{{ paymentSummary.totals.count || 0 }}</strong></span>
              <span class="col-status">
                <span v-if="paymentSummary.totals.all_calculated" class="status-badge complete">
                  <i class="pi pi-check-circle"></i> {{ t('Completo') }}
                </span>
                <span v-else class="status-badge pending">
                  {{ paymentSummary.totals.calculated || 0 }}/{{ paymentSummary.totals.count || 0 }}
                </span>
              </span>
              <span class="col-amount highlight-total">
                <strong>{{ n(parseFloat(String(paymentSummary.totals.net_amount ?? 0)) || 0, 'currency') }}</strong>
              </span>
            </div>
          </div>
        </div>

        <!-- Empty State when no calculations -->
        <div v-else-if="!isLoading" class="empty-state">
          <i class="pi pi-calculator"></i>
          <p>{{ t('Nenhum cálculo realizado ainda') }}</p>
          <span class="empty-hint">{{ t('Clique no botão abaixo para iniciar os cálculos') }}</span>
        </div>

        <!-- Action Button -->
        <div class="action-section">
          <Button
            data-testid="payroll-step5-action-button"
            :label="buttonLabel"
            :icon="buttonIcon"
            @click="openPaymentWizard"
            :severity="buttonSeverity"
            size="large"
            class="action-button"
            :loading="isLoading" />
        </div>

        <!-- Info Messages -->
        <div v-if="statusAsNumber < PERIOD_STATUS.CALCULATED" class="info-message pending">
          <i class="pi pi-info-circle"></i>
          <span>{{ t('Clique no botão acima para calcular os pagamentos de cada tipo de regra.') }}</span>
        </div>

        <div v-else-if="statusAsNumber === PERIOD_STATUS.CALCULATED" class="info-message success">
          <i class="pi pi-check-circle"></i>
          <span>{{ t('Cálculos realizados com sucesso! Clique em "Próximo" para confirmar e criar as despesas de pagamento.') }}</span>
        </div>

        <div v-else-if="statusAsNumber === PERIOD_STATUS.CONFIRMED" class="confirmed-warning-section">
          <inline-message severity="warn" :closable="false" class="reconfirm-warning">
            <template #icon>
              <i class="pi pi-exclamation-triangle" style="font-size: 1.5rem"></i>
            </template>
            <div class="warning-content">
              <h4 class="warning-title">{{ t('Período Já Confirmado') }}</h4>
              <p class="warning-text">
                {{ t('Este período já foi confirmado e as despesas de salário foram criadas.') }}
              </p>
              <ul class="warning-list">
                <li>{{ t('As despesas pendentes estão disponíveis para pagamento') }}</li>
                <li>{{ t('Você pode adicionar deduções e clicar em "Confirmar" novamente') }}</li>
                <li>{{ t('Reconfirmar irá SUBSTITUIR as despesas pendentes anteriores') }}</li>
                <li>{{ t('Despesas já pagas NÃO serão alteradas') }}</li>
              </ul>
            </div>
          </inline-message>
        </div>

        <div v-else-if="statusAsNumber === PERIOD_STATUS.PAID" class="info-message completed">
          <i class="pi pi-check-circle"></i>
          <span>{{ t('Todos os pagamentos foram realizados com sucesso!') }}</span>
        </div>
      </div>
    </div>

    <!-- PayrollCalculationsDialog (replaces nested wizard) -->
    <PayrollCalculationsDialog
      v-if="periodId !== null && franchiseId !== null"
      v-model:visible="showPaymentWizard"
      :period-id="periodId as number"
      :franchise-id="franchiseId as number"
      :rules="rules"
      @calculations-complete="onCalculationsComplete"
      @closed="onWizardClosed" />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch, inject } from 'vue';
import { useI18n } from 'vue-i18n';
import PayrollCalculationsDialog from '../dialogs/PayrollCalculationsDialog.vue';
import { usePayrollCalculations } from '@/composables/usePayrollCalculations';
import {
  PERIOD_STATUS,
  updatePeriodStatus,
  getWizardState,
  getStatusLabel
} from '@/composables/usePayrollPeriods';
import { STEP_STATUS } from '@/composables/usePayrollStatus';
import { eventBus } from '@/eventBus';
import { log, statusTrace } from '@/utils/DebugLogger';
import type { Payroll_MarkPeriodCalculatedResponseDados } from '@/contracts/Payroll_MarkPeriodCalculatedResponse';

// =============================================================================
// TYPES
// =============================================================================

type Id = number | string;

interface PeriodCalculationStep {
  rule_type: string;
  label: string;
  count: number;
  net_amount: number | string;
  base_amount?: number | string;
  is_complete: boolean;
}

interface UserExecution {
  rule_type: string;
  net_amount?: number | string;
  base_amount?: number | string;
  [key: string]: unknown;
}

interface UserBreakdown {
  user_id: Id;
  user_name?: string;
  total: number | string;
  executions: UserExecution[];
}

interface PaymentRule {
  rule_type: string;
  [key: string]: unknown;
}

interface PaymentSummaryTotals {
  count: number;
  calculated: number;
  base_amount: number | string;
  net_amount: number | string;
  all_calculated: boolean;
}

interface PaymentSummary {
  status: number;
  ordered_steps: PeriodCalculationStep[];
  by_user: UserBreakdown[];
  totals: PaymentSummaryTotals;
}

interface CalculationSummaryApiResponse {
  period?: { status?: number | string };
  ordered_steps?: PeriodCalculationStep[];
  by_user?: UserBreakdown[];
  totals?: PaymentSummaryTotals;
}

type MarkCalculatedResult = Payroll_MarkPeriodCalculatedResponseDados;

interface CalculationCompletePayload {
  executedTotal?: number;
  soldRemotelyTotal?: number;
  fixedTotal?: number;
  grandTotal?: number;
}

interface WizardMachine {
  context: { value: {
    period?: {
      id?: Id;
      franchise_id?: Id;
      status?: number | string;
    };
    periodFormData?: {
      franchise_id?: Id | null;
    };
    rules?: PaymentRule[];
  } };
  markCalculationsDone: (data: {
    executed: number;
    soldRemotely: number;
    fixed: number;
    total: number;
    by_user: UserBreakdown[];
    status: number;
  }) => void;
}

interface DataTableColumnConfig {
  data: string;
  title: string;
  sortable?: boolean;
  cellRenderer?: (params: { data: UserExecution; value?: unknown }) => HTMLElement | string;
  renderType?: string;
  dataType?: string;
}

interface DataTableConfig {
  tableId: string;
  pagination: boolean;
  columns: DataTableColumnConfig[];
}

// =============================================================================
// INJECT MACHINE - Single Source of Truth
// =============================================================================

const machine = inject<WizardMachine>('wizardMachine')!;

// =============================================================================
// REACTIVE DATA FROM MACHINE
// =============================================================================

/**
 * Get period ID from machine context (converted to Number for prop type safety)
 */
const periodId = computed<number | null>(() => {
  const id = machine.context.value.period?.id;
  return id ? Number(id) : null;
});

/**
 * Get franchise ID from machine context
 */
const franchiseId = computed<number | null>(() => {
  return Number(machine.context.value.period?.franchise_id) ||
         Number(machine.context.value.periodFormData?.franchise_id) ||
         null;
});

/**
 * Get period status from machine context
 */
const periodStatus = computed<number | string>(() => {
  return machine.context.value.period?.status ?? PERIOD_STATUS.OPEN;
});

/**
 * Get rules from machine context
 */
const rules = computed<PaymentRule[]>(() => machine.context.value.rules || []);

// =============================================================================
// STATUS CONSTANTS - imported from usePayrollPeriods (single source of truth)
// =============================================================================
// Status values: OPEN(0) → REVENUE(1) → EXPENSES(2) → CALCULATED(3) → CONFIRMED(4) → PAID(5), ARCHIVED(-1)
//
// PREFERRED: Use wizardState computed for step states and enablement checks
// LEGACY: Use PERIOD_STATUS constants only when updating status from API responses

// Composables
const { t, n } = useI18n();
const { getPeriodCalculationSummary, markPeriodCalculated, isLoading } = usePayrollCalculations();

// State
const paymentSummary = ref<PaymentSummary>({
  status: PERIOD_STATUS.OPEN,
  ordered_steps: [],
  by_user: [],
  totals: {
    count: 0,
    calculated: 0,
    base_amount: 0,
    net_amount: 0,
    all_calculated: false
  }
});
const showPaymentWizard = ref<boolean>(false);

// Numeric coercion of paymentSummary.status, used in template guards
const statusAsNumber = computed<number>(() => {
  const raw = paymentSummary.value.status;
  if (typeof raw === 'number') return raw;
  const parsed = parseInt(String(raw ?? ''), 10);
  return Number.isNaN(parsed) ? PERIOD_STATUS.OPEN : parsed;
});

// Centralized wizard state for semantic status checks
const wizardState = computed(() => {
  // Build a minimal period object for getWizardState
  const periodForState = {
    id: periodId.value,
    status: paymentSummary.value.status
  };
  const state = getWizardState(periodForState);
  log('Step5PaymentRulesConciliationSummary', 'wizardState', 'Computed wizard state', {
    periodId: periodId.value,
    status: paymentSummary.value.status,
    state
  });
  return state;
});

// Computed
const statusClass = computed(() => {
  // Use wizardState for semantic status checks
  return {
    'pending': wizardState.value.paymentRules === STEP_STATUS.PENDING,
    'calculated': wizardState.value.paymentRules === STEP_STATUS.COMPLETED && !('isConfirmationEnabled' in wizardState.value ? wizardState.value.isConfirmationEnabled : false),
    'in-progress': wizardState.value.confirmation === STEP_STATUS.IN_PROGRESS,
    'completed': wizardState.value.isPaid
  };
});

const statusText = computed<string>(() => {
  // Use centralized getStatusLabel for consistent labeling
  return getStatusLabel(paymentSummary.value.status);
});

const buttonLabel = computed<string>(() => {
  // Use wizardState for semantic status checks
  if (wizardState.value.isPaid) return t('Ver Pagamentos');
  if (('isConfirmationEnabled' in wizardState.value ? wizardState.value.isConfirmationEnabled : false)) return t('Ver Detalhes');
  if (wizardState.value.paymentRules === STEP_STATUS.COMPLETED) return t('Recalcular');
  return t('Iniciar Cálculos');
});

const buttonIcon = computed<string>(() => {
  // Use wizardState for semantic status checks
  if (wizardState.value.isPaid) return 'pi pi-eye';
  if (('isConfirmationEnabled' in wizardState.value ? wizardState.value.isConfirmationEnabled : false)) return 'pi pi-arrow-right';
  if (wizardState.value.paymentRules === STEP_STATUS.COMPLETED) return 'pi pi-refresh';
  return 'pi pi-calculator';
});

const buttonSeverity = computed<'primary' | 'secondary' | 'warning'>(() => {
  // Use wizardState for semantic status checks
  if (wizardState.value.isPaid) return 'secondary';
  if (wizardState.value.paymentRules === STEP_STATUS.COMPLETED) return 'warning';
  return 'primary';
});

// Methods
const getRuleTypeIcon = (ruleType: string): string => {
  const icons: Record<string, string> = {
    'commission_over_gross_revenue_executed': 'pi pi-percentage',
    'commission_over_gross_revenue_sold_remotely': 'pi pi-phone',
    'commission_over_gross_revenue_sold_in_loco': 'pi pi-map-marker',
    'fixed': 'pi pi-wallet'
  };
  return icons[ruleType] || 'pi pi-circle';
};

const getRuleTypeLabel = (ruleType: string): string => {
  const labels: Record<string, string> = {
    'commission_over_gross_revenue_executed': t('Comissão Executado'),
    'commission_over_gross_revenue_sold_remotely': t('Comissão Vendido Remoto'),
    'commission_over_gross_revenue_sold_in_loco': t('Comissão Vendido Presencial'),
    'fixed': t('Fixo')
  };
  return labels[ruleType] || ruleType;
};

// Cell renderer for the rule_type column inside each user's executions table.
// Returns an HTMLElement that the data-table renders directly into the cell.
const renderRuleTypeCell = (params: { data: UserExecution }): HTMLElement => {
  const ruleType = params.data?.rule_type ?? '';
  const wrapper = document.createElement('div');
  wrapper.className = 'rule-type-cell';

  const icon = document.createElement('i');
  icon.className = `${getRuleTypeIcon(ruleType)} rule-type-icon`;

  const label = document.createElement('span');
  label.textContent = getRuleTypeLabel(ruleType);

  wrapper.appendChild(icon);
  wrapper.appendChild(label);
  return wrapper;
};

// Cell renderer for the value column — preserves the parseFloat fallback
// behaviour: prefer net_amount, fall back to base_amount, then 0.
const renderUserAmountCell = (params: { data: UserExecution }): HTMLElement => {
  const data = params.data || ({} as UserExecution);
  const raw = data.net_amount ?? data.base_amount ?? 0;
  const value = parseFloat(String(raw ?? 0)) || 0;
  const strong = document.createElement('strong');
  strong.textContent = n(value, 'currency');
  return strong;
};

// Per-user data-table config (one table per accordion tab)
const getUserExecutionsConfig = (user: UserBreakdown): DataTableConfig => ({
  tableId: `payroll-step5-user-executions-${user.user_id ?? 'unknown'}`,
  pagination: false,
  columns: [
    {
      data: 'rule_type',
      title: t('Tipo'),
      sortable: false,
      cellRenderer: renderRuleTypeCell
    },
    {
      data: 'net_amount',
      title: t('Valor'),
      sortable: false,
      cellRenderer: renderUserAmountCell
    }
  ]
});

const loadPaymentSummary = async (): Promise<void> => {
  // STATUS_TRACE: Function entry - log periodStatus from machine context
  statusTrace('Step5Summary.loadPaymentSummary', 'FUNCTION_ENTRY', {
    periodId: periodId.value ?? undefined,
    status: periodStatus.value,
    franchiseId: franchiseId.value ?? undefined
  });

  if (!periodId.value || !franchiseId.value) {
    log('Step5PaymentRulesConciliationSummary', 'loadPaymentSummary', 'Missing machine context data');
    return;
  }

  log('Step5PaymentRulesConciliationSummary', 'loadPaymentSummary', 'Loading summary', {
    periodId: periodId.value,
    franchiseId: franchiseId.value
  });

  try {
    const summary = await getPeriodCalculationSummary(periodId.value, franchiseId.value) as CalculationSummaryApiResponse;

    // STATUS_TRACE: API response received - log the status from backend
    statusTrace('Step5Summary.loadPaymentSummary', 'API_RESPONSE', {
      periodId: periodId.value,
      status: summary?.period?.status,
      allCalculated: summary?.totals?.all_calculated
    });

    // Determine status:
    // 1. Use explicit period.status if available (now numeric)
    // 2. Else, derive from totals.all_calculated (true = PERIOD_STATUS.CALCULATED)
    // 3. Else, use periodStatus from machine context
    // 4. Default to PERIOD_STATUS.OPEN
    let derivedStatus: number;
    let derivationSource = 'period.status';
    const apiStatus = summary?.period?.status;
    if (apiStatus === undefined || apiStatus === null) {
      if (summary?.totals?.all_calculated) {
        derivedStatus = PERIOD_STATUS.CALCULATED;
        derivationSource = 'all_calculated=true';
      } else {
        derivedStatus = periodStatus.value !== undefined ? parseInt(String(periodStatus.value), 10) : PERIOD_STATUS.OPEN;
        derivationSource = periodStatus.value !== undefined ? 'machine.periodStatus' : 'default_OPEN';
      }
    } else {
      derivedStatus = parseInt(String(apiStatus), 10);
    }

    // STATUS_TRACE: CRITICAL - show how derivedStatus was calculated
    statusTrace('Step5Summary.loadPaymentSummary', 'DERIVED_STATUS', {
      periodId: periodId.value,
      status: derivedStatus,
      derivationSource,
      apiPeriodStatus: summary?.period?.status,
      machinePeriodStatus: periodStatus.value,
      allCalculated: summary?.totals?.all_calculated
    });

    // Map the response to our state
    paymentSummary.value = {
      status: derivedStatus,
      ordered_steps: summary?.ordered_steps || [],
      by_user: summary?.by_user || [],
      totals: summary?.totals || {
        count: 0,
        calculated: 0,
        base_amount: 0,
        net_amount: 0,
        all_calculated: false
      }
    };

    log('Step5PaymentRulesConciliationSummary', 'loadPaymentSummary', 'Summary loaded', paymentSummary.value);

    // If calculations are already complete, notify parent so it can enable navigation
    // Flag isAlreadyComplete: true so parent knows this is NOT a fresh calculation (no success message needed)
    if (derivedStatus >= PERIOD_STATUS.CALCULATED || summary?.totals?.all_calculated) {
      log('Step5PaymentRulesConciliationSummary', 'loadPaymentSummary', 'Calculations already complete, notifying via eventBus');

      // If all_calculated is true but status is not yet CALCULATED, update the backend status
      if (derivedStatus < PERIOD_STATUS.CALCULATED && summary?.totals?.all_calculated) {
        log('Step5PaymentRulesConciliationSummary', 'loadPaymentSummary', 'Status not yet CALCULATED, updating backend');

        // STATUS_TRACE: About to call markPeriodCalculated API
        statusTrace('Step5Summary.loadPaymentSummary', 'BEFORE_MARK_CALCULATED_API', {
          periodId: periodId.value,
          status: derivedStatus,
          reason: 'all_calculated=true but status<CALCULATED'
        });

        try {
          const result: MarkCalculatedResult | undefined = await markPeriodCalculated(periodId.value, franchiseId.value);

          // Sync status from API response to centralized store
          const newStatusRaw = result?.status ?? PERIOD_STATUS.CALCULATED;
          const newStatus = typeof newStatusRaw === 'number' ? newStatusRaw : parseInt(String(newStatusRaw), 10);

          // STATUS_TRACE: API succeeded - about to update store
          statusTrace('Step5Summary.loadPaymentSummary', 'MARK_CALCULATED_API_SUCCESS', {
            periodId: periodId.value,
            status: newStatus,
            apiResult: result
          });

          updatePeriodStatus(periodId.value, newStatus, 'mark_period_calculated');

          // Also update local state for immediate UI feedback
          paymentSummary.value.status = newStatus;
          log('Step5PaymentRulesConciliationSummary', 'loadPaymentSummary', 'Backend status updated', { newStatus });
        } catch (markError) {
          // STATUS_TRACE: API FAILED - but we may still emit event!
          const err = markError as Error;
          statusTrace('Step5Summary.loadPaymentSummary', 'MARK_CALCULATED_API_FAILED', {
            periodId: periodId.value,
            status: derivedStatus,
            error: err?.message || String(markError)
          });

          log('Step5PaymentRulesConciliationSummary', 'loadPaymentSummary', 'Error updating status', markError);
          console.error('Error marking period as calculated:', markError);
        }
      }

      // STATUS_TRACE: CRITICAL - About to notify via eventBus
      statusTrace('Step5Summary.loadPaymentSummary', 'BEFORE_EVENTBUS_CALCULATIONS_COMPLETE', {
        periodId: periodId.value,
        status: paymentSummary.value.status,
        isAlreadyComplete: true
      });

      // Notify via eventBus instead of emit
      eventBus.emit('payroll.calculations.complete', {
        executed: 0,
        soldRemotely: 0,
        fixed: 0,
        total: summary?.totals?.net_amount || 0,
        isAlreadyComplete: true,
        status: paymentSummary.value.status // Include status from API response for parent sync
      });
    }
  } catch (error) {
    log('Step5PaymentRulesConciliationSummary', 'loadPaymentSummary', 'Error', error);
    // Set default values on error - this is normal if no calculations exist yet
    paymentSummary.value = {
      status: periodStatus.value !== undefined ? parseInt(String(periodStatus.value), 10) : PERIOD_STATUS.OPEN,
      ordered_steps: [],
      by_user: [],
      totals: {
        count: 0,
        calculated: 0,
        base_amount: 0,
        net_amount: 0,
        all_calculated: false
      }
    };

    // STATUS_TRACE: API error - using fallback status
    const err = error as Error;
    statusTrace('Step5Summary.loadPaymentSummary', 'API_ERROR_FALLBACK', {
      periodId: periodId.value,
      status: paymentSummary.value.status,
      error: err?.message || String(error)
    });
  }
};

const openPaymentWizard = (): void => {
  if (!periodId.value || !franchiseId.value) {
    log('Step5PaymentRulesConciliationSummary', 'openPaymentWizard', 'Missing machine context data', {
      periodId: periodId.value,
      franchiseId: franchiseId.value
    });
    eventBus.emit('globalMessage.warning', t('Please complete previous steps first'));
    return;
  }

  log('Step5PaymentRulesConciliationSummary', 'openPaymentWizard', 'Opening wizard', {
    periodId: periodId.value,
    franchiseId: franchiseId.value
  });
  showPaymentWizard.value = true;
};

const onWizardClosed = async (): Promise<void> => {
  log('Step5PaymentRulesConciliationSummary', 'onWizardClosed', 'Wizard closed');

  // Reload summary to reflect any changes
  await loadPaymentSummary();
};

/**
 * Handle calculations-complete event from nested wizard.
 * This is emitted when all calculation steps are done in the nested wizard.
 * We notify the machine so it can transition to summary state.
 */
const onCalculationsComplete = async (calculationData: CalculationCompletePayload): Promise<void> => {
  log('Step5PaymentRulesConciliationSummary', 'onCalculationsComplete', 'Calculations complete', calculationData);

  // Reload summary to get updated status
  await loadPaymentSummary();

  // Sync status to centralized store after reload
  if (periodId.value && paymentSummary.value.status !== undefined) {
    updatePeriodStatus(periodId.value, paymentSummary.value.status, 'nested_wizard_calculations_complete');
  }

  // Tell the machine calculations are done - this will transition to summary
  // and update the machine's context with calculation results
  machine.markCalculationsDone({
    executed: calculationData?.executedTotal || 0,
    soldRemotely: calculationData?.soldRemotelyTotal || 0,
    fixed: calculationData?.fixedTotal || 0,
    total: calculationData?.grandTotal || 0,
    by_user: paymentSummary.value.by_user || [],
    status: paymentSummary.value.status
  });

  // Also notify via eventBus for any other listeners
  eventBus.emit('payroll.calculations.complete', {
    executed: calculationData?.executedTotal || 0,
    soldRemotely: calculationData?.soldRemotelyTotal || 0,
    fixed: calculationData?.fixedTotal || 0,
    total: calculationData?.grandTotal || 0,
    status: paymentSummary.value.status
  });
};


// Watch for machine context changes
watch(() => [periodId.value, franchiseId.value], ([newPeriodId, newFranchiseId]) => {
  if (newPeriodId && newFranchiseId) {
    log('Step5PaymentRulesConciliationSummary', 'watch:context', 'Machine context changed, loading summary', {
      periodId: newPeriodId,
      franchiseId: newFranchiseId
    });
    loadPaymentSummary();
  }
}, { immediate: true });

watch(() => periodStatus.value, (newStatus) => {
  if (newStatus !== undefined && newStatus !== null) {
    paymentSummary.value.status = parseInt(String(newStatus), 10);
  }
});

// Lifecycle
onMounted(() => {
  log('Step5PaymentRulesConciliationSummary', 'mounted', 'Component mounted', {
    periodId: periodId.value,
    franchiseId: franchiseId.value
  });
  // Note: loadPaymentSummary is called by the watch with { immediate: true }
  // so we don't need to call it here (that would cause duplicate loads/events)
});
</script>

<style scoped>
/* Step Header */
.step-header h2 {
  margin: 0 0 1rem 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-color);
}

/* Helper Banner */
.helper-banner {
  display: flex;
  gap: 1rem;
  padding: 1rem;
  background: var(--blue-50);
  border-left: 4px solid var(--blue-500);
  border-radius: 6px;
  margin-bottom: 1rem;
}

.helper-banner i {
  color: var(--blue-500);
  font-size: 1.5rem;
  flex-shrink: 0;
}

.helper-content strong {
  display: block;
  margin-bottom: 0.5rem;
  color: var(--blue-700);
}

.helper-content ul {
  margin: 0;
  padding-left: 1.25rem;
}

.helper-content li {
  margin-bottom: 0.25rem;
  color: var(--blue-900);
  font-size: 0.9rem;
}

/* Warning Banner */
.warning-banner {
  display: flex;
  gap: 1rem;
  padding: 1rem;
  background: var(--orange-50);
  border-left: 4px solid var(--orange-500);
  border-radius: 6px;
  margin-bottom: 1rem;
}

.warning-banner i {
  color: var(--orange-500);
  font-size: 1.5rem;
  flex-shrink: 0;
}

.warning-content strong {
  display: block;
  margin-bottom: 0.25rem;
  color: var(--orange-700);
}

.warning-content p {
  margin: 0;
  color: var(--orange-900);
  font-size: 0.9rem;
  line-height: 1.5;
}

/* Important Notes */
.important-notes {
  padding: 1.25rem;
  background: var(--surface-0);
  border: 1px solid var(--surface-border);
  border-radius: 8px;
  margin-bottom: 1rem;
}

.notes-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid var(--surface-border);
}

.notes-header i {
  font-size: 1.25rem;
  color: var(--primary-color);
}

.notes-header h3 {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-color);
}

.notes-list {
  margin: 0;
  padding-left: 0;
  list-style: none;
}

.notes-list li {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
  font-size: 0.9rem;
  color: var(--text-color);
  line-height: 1.5;
}

.notes-list li i {
  color: var(--primary-color);
  flex-shrink: 0;
  margin-top: 0.1rem;
}

.step-panel {
  width: 100%;
  padding: 1.5rem;
}

.step-description {
  margin-bottom: 1.5rem;
  color: var(--text-color-secondary);
}

/* Conciliation Card */
.conciliation-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  border: 1px solid var(--surface-border);
  overflow: hidden;
}

.card-body {
  padding: 2rem;
}

/* Summary Grid */
.summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.summary-item {
  padding: 1.25rem;
  background: var(--surface-50);
  border-radius: 8px;
  border: 1px solid var(--surface-border);
  transition: all 0.3s ease;
}

.summary-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.summary-item.highlight {
  background: linear-gradient(135deg, var(--primary-50) 0%, var(--primary-100) 100%);
  border-color: var(--primary-200);
}

.item-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-color-secondary);
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.item-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text-color);
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.summary-item.highlight .item-value {
  color: var(--primary-color);
  font-size: 1.75rem;
}

.status-indicator {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  display: inline-block;
}

.status-indicator.pending {
  background: var(--yellow-500);
  animation: pulse-yellow 2s infinite;
}

.status-indicator.calculated {
  background: var(--blue-500);
  animation: pulse-blue 2s infinite;
}

.status-indicator.in-progress {
  background: var(--orange-500);
  animation: pulse-orange 2s infinite;
}

.status-indicator.completed {
  background: var(--green-500);
}

@keyframes pulse-yellow {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

@keyframes pulse-blue {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

@keyframes pulse-orange {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

/* User-Centered Accordion View */
.user-accordion {
  margin-bottom: 1.5rem;
}

.user-accordion :deep(.p-accordion-header-link) {
  padding: 1rem 1.25rem;
  background: var(--surface-50);
  border: 1px solid var(--surface-border);
  border-radius: 8px;
  margin-bottom: 0.5rem;
  transition: all 0.2s ease;
}

.user-accordion :deep(.p-accordion-header-link:hover) {
  background: var(--surface-100);
}

.user-accordion :deep(.p-accordion-content) {
  padding: 0;
  border: none;
  background: transparent;
}

.user-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  gap: 1rem;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.user-info i {
  font-size: 1.25rem;
  color: var(--primary-500);
  background: var(--primary-100);
  padding: 0.5rem;
  border-radius: 50%;
}

.user-name {
  font-weight: 600;
  font-size: 1rem;
  color: var(--text-color);
}

.user-total-tag {
  font-size: 1rem;
  font-weight: 700;
  padding: 0.5rem 1rem;
}

/* Executions Table inside Accordion */
.executions-table {
  margin: 0.75rem 0 1rem 0;
  border-radius: 8px;
  overflow: hidden;
}

.executions-table :deep(.p-datatable-thead > tr > th) {
  background: var(--surface-100);
  padding: 0.75rem 1rem;
  font-size: 0.8125rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.executions-table :deep(.p-datatable-tbody > tr > td) {
  padding: 0.75rem 1rem;
  font-size: 0.9375rem;
}

.executions-table :deep(.p-datatable-tbody > tr:hover) {
  background: var(--surface-50);
}

.rule-type-cell {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.rule-type-icon {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--primary-100);
  color: var(--primary-600);
  border-radius: 6px;
  font-size: 0.875rem;
}

/* Totals Summary Section */
.totals-summary {
  background: var(--surface-100);
  border-radius: 8px;
  padding: 1rem 1.25rem;
  margin-top: 1rem;
  border: 1px solid var(--surface-border);
}

.totals-summary .totals-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
}

.totals-summary .totals-row:not(:last-child) {
  border-bottom: 1px solid var(--surface-200);
}

.totals-summary .totals-row.highlight {
  padding-top: 0.75rem;
  margin-top: 0.25rem;
}

.totals-label {
  font-weight: 500;
  color: var(--text-color-secondary);
}

.totals-value {
  font-weight: 600;
  color: var(--text-color);
}

.totals-summary .totals-row.highlight .totals-label {
  font-size: 1rem;
  color: var(--text-color);
}

.totals-summary .totals-row.highlight .totals-value {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--primary-600);
}

/* Breakdown Section */
.breakdown-section {
  margin-bottom: 2rem;
}

.breakdown-title {
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-color-secondary);
  margin: 0 0 1rem 0;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid var(--surface-border);
}

.breakdown-table {
  background: var(--surface-50);
  border-radius: 8px;
  border: 1px solid var(--surface-border);
  overflow: hidden;
}

.breakdown-header {
  display: grid;
  grid-template-columns: 2fr 0.75fr 1.25fr 1fr;
  gap: 1rem;
  padding: 0.875rem 1.25rem;
  background: var(--surface-100);
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--text-color-secondary);
  border-bottom: 1px solid var(--surface-border);
}

.breakdown-row {
  display: grid;
  grid-template-columns: 2fr 0.75fr 1.25fr 1fr;
  gap: 1rem;
  padding: 1rem 1.25rem;
  align-items: center;
  border-bottom: 1px solid var(--surface-200);
  transition: background-color 0.2s ease;
}

.breakdown-row:last-child {
  border-bottom: none;
}

.breakdown-row:hover:not(.totals-row) {
  background: var(--surface-100);
}

.breakdown-row.totals-row {
  background: var(--surface-100);
  border-top: 2px solid var(--surface-300);
}

.col-type {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-weight: 500;
}

.type-icon {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--primary-100);
  color: var(--primary-600);
  border-radius: 6px;
  font-size: 0.875rem;
}

.col-count {
  text-align: center;
}

.col-status {
  text-align: center;
}

.col-amount {
  text-align: right;
  font-weight: 600;
}

.highlight-total {
  color: var(--primary-600);
  font-size: 1.125rem;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.25rem 0.625rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
}

.status-badge.complete {
  background: var(--green-100);
  color: var(--green-700);
}

.status-badge.pending {
  background: var(--yellow-100);
  color: var(--yellow-700);
}

.status-badge i {
  font-size: 0.625rem;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 2.5rem 1.5rem;
  color: var(--text-color-secondary);
}

.empty-state i {
  font-size: 3rem;
  color: var(--surface-400);
  margin-bottom: 1rem;
}

.empty-state p {
  margin: 0 0 0.5rem 0;
  font-size: 1.125rem;
  font-weight: 500;
  color: var(--text-color);
}

.empty-hint {
  font-size: 0.875rem;
}

/* Action Section */
.action-section {
  display: flex;
  justify-content: center;
  padding: 1.5rem 0;
  margin-bottom: 1rem;
}

.action-button {
  min-width: 250px;
  font-size: 1.125rem;
  padding: 0.875rem 2rem;
}

/* Info Messages */
.info-message {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.25rem;
  border-radius: 8px;
  font-size: 0.9375rem;
  line-height: 1.5;
}

.info-message i {
  font-size: 1.25rem;
  flex-shrink: 0;
}

.info-message.pending {
  background: var(--yellow-50);
  border: 1px solid var(--yellow-200);
  color: var(--yellow-900);
}

.info-message.pending i {
  color: var(--yellow-600);
}

.info-message.in-progress {
  background: var(--blue-50);
  border: 1px solid var(--blue-200);
  color: var(--blue-900);
}

.info-message.in-progress i {
  color: var(--blue-600);
}

.info-message.success {
  background: var(--green-50);
  border: 1px solid var(--green-200);
  color: var(--green-900);
}

.info-message.success i {
  color: var(--green-600);
}

.info-message.completed {
  background: var(--green-50);
  border: 1px solid var(--green-200);
  color: var(--green-900);
}

.info-message.completed i {
  color: var(--green-600);
}

/* Confirmed Period Warning */
.confirmed-badge {
  margin-left: 0.75rem;
  font-weight: 600;
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
}

.confirmed-warning-section {
  margin-top: 2rem;
}

.reconfirm-warning {
  font-size: 0.95rem;
}

.warning-content {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.warning-title {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--yellow-900);
}

.warning-text {
  margin: 0;
  color: var(--yellow-800);
  line-height: 1.5;
}

.warning-list {
  margin: 0.5rem 0 0 0;
  padding-left: 1.5rem;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.warning-list li {
  color: var(--yellow-800);
  line-height: 1.6;
  position: relative;
  padding-left: 0.5rem;
}

.warning-list li::before {
  content: '';
  position: absolute;
  left: -1rem;
  top: 0.5rem;
  width: 6px;
  height: 6px;
  background: var(--yellow-600);
  border-radius: 50%;
}

/* Responsive */
@media (max-width: 768px) {
  .card-body {
    padding: 1.5rem;
  }

  .summary-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .item-value {
    font-size: 1.25rem;
  }

  .summary-item.highlight .item-value {
    font-size: 1.5rem;
  }

  .breakdown-header,
  .breakdown-row {
    grid-template-columns: 1.5fr 0.5fr 1fr 1fr;
    gap: 0.5rem;
    padding: 0.75rem 1rem;
    font-size: 0.875rem;
  }

  .breakdown-header {
    font-size: 0.625rem;
  }

  .type-icon {
    display: none;
  }

  .action-button {
    min-width: 100%;
    font-size: 1rem;
  }

  /* User-centered view mobile styles */
  .user-accordion :deep(.p-accordion-header-link) {
    padding: 0.75rem 1rem;
  }

  .user-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }

  .user-total-tag {
    font-size: 0.875rem;
    padding: 0.375rem 0.75rem;
  }

  .executions-table :deep(.p-datatable-thead > tr > th),
  .executions-table :deep(.p-datatable-tbody > tr > td) {
    padding: 0.5rem 0.75rem;
    font-size: 0.8125rem;
  }

  .rule-type-icon {
    width: 24px;
    height: 24px;
    font-size: 0.75rem;
  }

  .totals-summary {
    padding: 0.75rem 1rem;
  }

  .totals-summary .totals-row.highlight .totals-value {
    font-size: 1.125rem;
  }
}
</style>
